import * as s from './schema'

import * as Apollo from '@apollo/client/react'

export type BulletinsSearch = {
	modelType: 'bulletins'
	modelId: number
	bulletin: s.SearchQuery['search'][number]['bulletin']
}
export type ResourcesSearch = {
	modelType: 'resources'
	modelId: number
	resource: s.SearchQuery['search'][number]['resource']
}
export type UsersSearch = {
	modelType: 'users'
	modelId: number
	user: s.SearchQuery['search'][number]['user']
}
export type Search = BulletinsSearch | ResourcesSearch | UsersSearch

export function useSearchQuery(baseOptions: Apollo.QueryHookOptions<s.SearchQuery, s.SearchQueryVariables>) {
	const { data, loading, error } = s.useSearchQuery(baseOptions)

	let typedData: Search[]
	if (data) {
		typedData = data.search.map((searchData) => {
			switch (searchData.modelType) {
				case 'bulletin':
					return searchData as BulletinsSearch
				case 'resources':
					return searchData as ResourcesSearch
				case 'users':
					return searchData as UsersSearch
			}
		})
	}
	return { data: typedData ? { search: typedData } : null, loading, error }
}

export type GalleryMediaItem =
	| { type: 'video'; url: string; thumbnailUrl: string }
	| { type: 'photo'; id: number; url: string }

export function useOrderedGetGalleryQuery(id: number, skip?: boolean) {
	const { data, loading, error } = s.useGetGalleryQuery({ variables: { id }, skip })

	if (data?.gallery) {
		const gallery = data.gallery
		let orderedPhotos = gallery.photos || []

		if (orderedPhotos.length > 0) {
			const lookup = {}
			const idList = gallery.children as number[]
			idList.forEach((id, idx) => (lookup[id] = idx))
			orderedPhotos = [...orderedPhotos].sort((a, b) => {
				return (lookup[a.id] || -1) - (lookup[b.id] || -1)
			})
		}

		const media: GalleryMediaItem[] = []

		// Video always first
		if ((gallery as any).videoUpload?.publicUrl) {
			media.push({
				type: 'video',
				url: (gallery as any).videoUpload.publicUrl,
				thumbnailUrl: (gallery as any).videoThumbnail?.publicUrl || ''
			})
		}

		orderedPhotos.forEach((photo) => {
			if (photo.image?.publicUrl) {
				media.push({ type: 'photo', id: photo.id, url: photo.image.publicUrl })
			}
		})

		return {
			loading,
			error,
			data: { ...data, gallery: { ...data.gallery, photos: orderedPhotos } },
			media
		}
	}
	return { data, loading, error, media: [] as GalleryMediaItem[] }
}
export function useOrderedGalleriesGetAllQuery() {
	const { data, loading, error } = s.useGalleriesGetAllQuery()

	if (data?.galleries?.length > 0) {
		const lookup = {}
		const idList = data.galleryPage.children as number[]
		idList.forEach((id, idx) => (lookup[id] = idx))

		const orderedGalleries = [...data.galleries].sort((a, b) => {
			return (lookup[a.id] || -1) - (lookup[b.id] || -1)
		})

		return { loading, error, data: { ...data, galleries: orderedGalleries } }
	}

	return { data, loading, error }
}

export type DepartmentDetailsFragment = Omit<s.DepartmentDetailsFragment, 'bulletins'> & {
	bulletins: s.DepartmentDetailsFragment['bulletins'][number]['bulletin'][]
}

export function useDepartmentDetailsQuery(
	baseOptions: Apollo.QueryHookOptions<s.DepartmentDetailsQuery, s.DepartmentDetailsQueryVariables>
) {
	const { data, loading, error } = s.useDepartmentDetailsQuery(baseOptions)

	if (data) {
		return {
			loading,
			error,
			data: {
				department: {
					...data.department,
					bulletins: data.department.bulletins.filter((b) => Boolean(b.bulletin?.id)).map((b) => b.bulletin)
				}
			}
		}
	}

	return { data: null, loading, error }
}

export type AddCommentMutationVariablesStrict =
	| { comment: string; bulletinId: number }
	| { comment: string; discussionId: number }
	| { comment: string; nominationId: number }

export function useAddCommentMutation(
	baseOptions?: Apollo.MutationHookOptions<s.AddCommentMutation, AddCommentMutationVariablesStrict>
) {
	return Apollo.useMutation<s.AddCommentMutation, AddCommentMutationVariablesStrict>(
		s.AddCommentDocument,
		baseOptions
	)
}
